import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { ButtonGroupWithDropdown } from "@dashboard/components/ButtonGroupWithDropdown";
import { DashboardCard } from "@dashboard/components/Card";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { extensionMountPoints } from "@dashboard/extensions/extensionMountPoints";
import {
  getExtensionItemsForOverviewCreate,
  getExtensionsItemsForMenuOverviewActions,
} from "@dashboard/extensions/getExtensionsItems";
import { useExtensions } from "@dashboard/extensions/hooks/useExtensions";
import { MenuFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { sectionNames } from "@dashboard/intl";
import { menuListUrl, MenuListUrlSortField } from "@dashboard/structures/urls";
import { ListActions, PageListProps, SortPage } from "@dashboard/types";
import { Box, Button, Input, PlusIcon, Text } from "@saleor/macaw-ui-next";
import { Search } from "lucide-react";
import React, { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import MenuList from "../MenuList";

export interface MenuListPageProps
  extends PageListProps,
    ListActions,
    SortPage<MenuListUrlSortField> {
  menus: MenuFragment[];
  onDelete: (id: string) => void;
  selectedMenuIds: string[];
}

const MenuListPage = ({ selectedMenuIds, menus, ...listProps }: MenuListPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const [searchQuery, setSearchQuery] = useState("");

  const handleCreateMenu = () => {
    navigate(menuListUrl({ action: "add" }));
  };

  const filteredMenus = useMemo(() => {
    if (!menus || !searchQuery.trim()) return menus || [];
    
    const query = searchQuery.toLowerCase();

    return menus.filter(menu => 
      menu.name.toLowerCase().includes(query)
    );
  }, [menus, searchQuery]);

  const { MENU_OVERVIEW_CREATE, MENU_OVERVIEW_MORE_ACTIONS } = useExtensions(
    extensionMountPoints.MENU_LIST,
  );

  const extensionMenuItems = getExtensionsItemsForMenuOverviewActions(
    MENU_OVERVIEW_MORE_ACTIONS,
    selectedMenuIds,
  );
  const extensionCreateButtonItems = getExtensionItemsForOverviewCreate(MENU_OVERVIEW_CREATE);

  return (
    <DetailPageLayout gridTemplateColumns={1}>
      <div style={{padding:"24px 0px 0px 24px"}}>
        <TopNav 
          title={intl.formatMessage(sectionNames.structures)} 
          subtitle="Manage your store's navigation menus and structure" 
          display={"contents"} 
        />
      </div>
      <DetailPageLayout.Content>
        <DashboardCard>
          <Box padding={6}>
            <Box display="flex" gap={3} alignItems="center" marginBottom={6} justifyContent="space-between">
              <Box position="relative" style={{ flex: 1, maxWidth: 400 }}>
                <Input
                  type="text"
                  placeholder="Search menus..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{ paddingLeft: 40 }}
                />
                <Box
                  position="absolute"
                  left={3}
                  top={5}
                  style={{ transform: "translateY(-50%)" }}
                >
                  <Search size={16} color="#616161" />
                </Box>
              </Box>
              
              <Box display="flex" alignItems="center" gap={2}>
                {extensionMenuItems.length > 0 && <TopNav.Menu items={extensionMenuItems} />}
                {extensionCreateButtonItems.length > 0 ? (
                  <ButtonGroupWithDropdown
                    options={extensionCreateButtonItems}
                    onClick={handleCreateMenu}
                    data-test-id="add-menu"
                  >
                    <PlusIcon />
                    <FormattedMessage
                      id="tQ98Ky"
                      defaultMessage="Create menu"
                      description="button"
                    />
                  </ButtonGroupWithDropdown>
                ) : (
                  <Button onClick={handleCreateMenu} variant="primary" data-test-id="add-menu">
                    <PlusIcon />
                    <FormattedMessage
                      id="tQ98Ky"
                      defaultMessage="Create menu"
                      description="button"
                    />
                  </Button>
                )}
              </Box>
            </Box>

            {filteredMenus.length === 0 ? (
              <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center" 
                padding={12}
                style={{ minHeight: 300 }}
              >
                <Text size={4} color="default2" marginBottom={2}>
                  {searchQuery ? "No menus found" : "No navigation menus"}
                </Text>
                <Text size={2} color="default2">
                  {searchQuery ? "Try adjusting your search criteria" : "Create your first navigation menu to get started"}
                </Text>
              </Box>
            ) : (
              <MenuList {...listProps} menus={filteredMenus} />
            )}
          </Box>
        </DashboardCard>
      </DetailPageLayout.Content>
    </DetailPageLayout>
  );
};

export default MenuListPage;
